/**
 * Formatadores de apresentação, com guarda para valor ausente.
 *
 * Antes existiam **27 definições locais idênticas** de `formatCurrency`
 * espalhadas pelas features, nenhuma com guarda. Quando um campo vinha
 * `undefined` — versão de API diferente, campo novo ainda não servido,
 * resposta parcial — `Intl.NumberFormat.format(undefined)` devolve `"R$ NaN"`
 * e isso ia direto para a tela. Foi o que apareceu no Painel Executivo
 * renderizado: "R$ NaN" em dois cartões, "NaN itens vencidos" e o gráfico com
 * eixo "R$ NaN".
 *
 * Um valor que não existe é diferente de zero. `—` diz "sem dado"; `R$ 0,00`
 * afirmaria que o saldo é zero. Num ERP essa diferença importa.
 */

/** Exibido quando o valor não existe ou não é numérico. */
export const EMPTY = '—';

function isNum(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

export function formatCurrency(value: number | null | undefined): string {
  if (!isNum(value)) return EMPTY;
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

export function formatNumber(
  value: number | null | undefined,
  options?: Intl.NumberFormatOptions,
): string {
  if (!isNum(value)) return EMPTY;
  return new Intl.NumberFormat('pt-BR', options).format(value);
}

export function formatPercent(
  value: number | null | undefined,
  fractionDigits = 1,
): string {
  if (!isNum(value)) return EMPTY;
  return new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

/** `YYYY-MM-DD` — data civil, sem hora e sem fuso. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return EMPTY;
  /* `new Date('2026-01-01')` e meia-noite **UTC** por especificacao. Num fuso
     negativo (America/Sao_Paulo, -03:00) `toLocaleDateString` entao devolve o
     DIA ANTERIOR — e a aplicacao inteira exibia vencimento, emissao e data de
     lancamento um dia antes do que sao. Data sem hora e data civil: monta-se
     no fuso local. */
  const d =
    value instanceof Date
      ? value
      : DATE_ONLY.test(value)
        ? new Date(`${value}T00:00:00`)
        : new Date(value);
  if (Number.isNaN(d.getTime())) return EMPTY;
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return EMPTY;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return EMPTY;
  return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

/** Inteiro com separador de milhar, para contagem de itens. */
export function formatCount(value: number | null | undefined): string {
  if (!isNum(value)) return EMPTY;
  return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(value);
}

/**
 * CPF/CNPJ com a mascara do cadastro, a partir dos digitos.
 *
 * Vive aqui porque tres telas mostram documento (Diretorio de Contatos,
 * Clientes, Pedido de Compra) e o backend guarda so os digitos.
 */
export function formatDocument(value?: string | null, type?: 'CPF' | 'CNPJ'): string {
  if (!value) return EMPTY;
  const digits = value.replace(/\D/g, '');
  if ((type === 'CNPJ' || digits.length === 14) && digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  return value;
}
